// Computes an explicit `{latitude, longitude, zoom}` fit instead of handing
// raw points to `MapCanvas`'s own `fitBounds` (Google's own heuristic),
// because `fitBounds`'s chosen zoom is a function of the container's own
// pixel dimensions — the tile's narrower, shorter container made it pick a
// visibly looser zoom (200km scale bar) than the full `/map` page's wider
// container picks for the IDENTICAL bounds (100km scale bar), per a direct
// side-by-side screenshot comparison. This is the standard Web Mercator
// "zoom to fit" formula (the same one behind Google's own long-published
// `getBoundsZoomLevel` sample), reimplemented here as a pure function so the
// tile's zoom is deterministic and unit-testable without a live Maps SDK —
// `MapCanvas.tsx`/`fit.ts`/`MapTab.tsx` (the full page) are untouched, so
// this cannot change the full page's own behavior.

interface LatLng {
  latitude: number
  longitude: number
}

interface PixelSize {
  width: number
  height: number
}

const WORLD_DIM = { height: 256, width: 256 }
const ZOOM_MAX = 21

function latRad(lat: number): number {
  const sin = Math.sin((lat * Math.PI) / 180)
  // Clamped away from exactly ±1 — `Math.log` of 0 (at the poles) is -Infinity,
  // which no real marker's latitude ever reaches, but guards the formula
  // against ever producing one.
  const clamped = Math.min(Math.max(sin, -0.9999), 0.9999)
  return Math.log((1 + clamped) / (1 - clamped)) / 2
}

function zoomForFraction(
  pixelDim: number,
  worldDim: number,
  fraction: number
): number {
  if (fraction <= 0) return ZOOM_MAX
  return Math.floor(Math.log(pixelDim / worldDim / fraction) / Math.LN2)
}

// `padding` shrinks the EFFECTIVE container the formula fits into (so markers
// never sit flush against the tile's edge), mirroring `MapCanvas.tsx`'s own
// `FIT_BOUNDS_PADDING_PX` for the full page's `fitBounds` call.
export function getBoundsZoomLevel(
  points: LatLng[],
  containerPx: PixelSize,
  paddingPx = 0
): number {
  if (points.length === 0) return ZOOM_MAX

  const lats = points.map((p) => p.latitude)
  const lngs = points.map((p) => p.longitude)
  const north = Math.max(...lats)
  const south = Math.min(...lats)
  const east = Math.max(...lngs)
  const west = Math.min(...lngs)

  const effectiveWidth = Math.max(containerPx.width - paddingPx * 2, 1)
  const effectiveHeight = Math.max(containerPx.height - paddingPx * 2, 1)

  const latFraction = (latRad(north) - latRad(south)) / Math.PI
  const lngDiff = east - west
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

  const latZoom = zoomForFraction(
    effectiveHeight,
    WORLD_DIM.height,
    latFraction
  )
  const lngZoom = zoomForFraction(effectiveWidth, WORLD_DIM.width, lngFraction)

  return Math.max(Math.min(latZoom, lngZoom, ZOOM_MAX), 0)
}

// The midpoint of the bounding box — not a centroid of the points themselves
// (which would skew toward wherever points happen to cluster) — matching
// what `fitBounds` itself centers on: the rectangle, not the data.
export function getBoundsCenter(points: LatLng[]): LatLng {
  const lats = points.map((p) => p.latitude)
  const lngs = points.map((p) => p.longitude)
  return {
    latitude: (Math.max(...lats) + Math.min(...lats)) / 2,
    longitude: (Math.max(...lngs) + Math.min(...lngs)) / 2
  }
}
